import { Helmet } from "react-helmet-async";
import { useSettings } from "../contexts/SettingsContext";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
  structuredData?: Record<string, any>;
}

export default function SEO({
  title,
  description,
  keywords,
  ogImage = "/seeinaction.png",
  ogUrl = "https://de5.ai",
  twitterCard = "summary_large_image",
  structuredData,
}: SEOProps) {
  const { settings } = useSettings();

  const defaultTitle =
    settings.heroTitle || "de5.ai - Automate Your DeFi Future";
  const defaultDescription =
    settings.heroSubtitle ||
    "Build powerful DeFi automation workflows without coding. Connect your wallet, set your triggers, and let de5.ai handle the rest.";
  const defaultKeywords =
    "DeFi, automation, blockchain, trading, DCA, smart contracts, Web3, decentralized finance, crypto, Ethereum";

  const finalTitle = title ? `${title} | de5.ai` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "de5.ai",
    description: finalDescription,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web Browser",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    author: {
      "@type": "Organization",
      name: "de5.ai",
      url: "https://de5.ai",
    },
    ...structuredData,
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="de5.ai" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={ogUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="de5.ai" />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={ogUrl} />
      <meta property="twitter:title" content={finalTitle} />
      <meta property="twitter:description" content={finalDescription} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:creator" content="@de5ai" />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content={settings.primaryColor || "#e95c49"} />
      <meta
        name="msapplication-TileColor"
        content={settings.primaryColor || "#e95c49"}
      />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(defaultStructuredData)}
      </script>
    </Helmet>
  );
}
